import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, Inject, LOCALE_ID, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators, AbstractControl, UntypedFormControl, UntypedFormGroup, UntypedFormArray, FormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { COMPANY_INFO, COUNTRY_MASTER_STATE_LIST, SERVICETYPE_LIST_STATE, STAFF_STATE, STATSIG_STATE, TENANTS_INFO } from 'apps/ff-frontend/src/app/state/app.state';
import { BehaviorSubject, distinctUntilChanged, takeUntil, Subject, forkJoin, EMPTY, catchError, finalize, tap, delay, Observable, debounceTime, switchMap, of, map } from 'rxjs';
import { CountryISO, NgxIntlTelInputComponent, PhoneNumberFormat } from '@fitness-force/fitness-int-phone-code';
import { CONSTANTS_DATA } from '@fitness-force/constants';
import { ClientGender, ClientChannel, OpportunityStageForNonMember, OpportunityBoardType, TaskPriority, TaskAssignToType, OpportunitiesType, ClientRelation, ElasticProviderName, Roles } from '@fitness-force/enum';
import * as lpn from 'google-libphonenumber';
import { ClientService, OpportunityStageService, TaskCategoryService, OrganizationService, ToastMessageService, CommonService, OmniSearchService, AuthSevices, TimeconversionService } from '@fitness-force/api';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { MiniSearchComponent } from '@fitness-force/common/search';
import { ErrorMsgComponent } from '@fitness-force/errors';
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { AvatarModule } from 'primeng/avatar';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputMaskModule } from 'primeng/inputmask';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { DrawerModule } from 'primeng/drawer';
import { AssignedToComponent } from '@fitness-force/common/assigned-to';
import { CreateClientRequest, CreateOpportunityOnClientRequest, CreateProspectRequest, CreateProspectResponse, CreateTaskByOpportunityOnClientRequest } from '@fitness-force/models';
import { InputNumberModule } from 'primeng/inputnumber';
import { LANGUAGE, STATE_STAFF_LIST, ClientById, PlanSummaryCRM, PlanSummaryClient, PlanSummary } from '@fitness-force/models';
import Statsig from 'statsig-js';
import { SkeletonModule } from 'primeng/skeleton';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ImageCropDialogComponent } from '@fitness-force/common/crop';
import { WalkInTourComponent } from '@fitness-force/shared';
import { WalkInTrialComponent } from '@fitness-force/shared';
import { CreateOpportunityComponent } from '@fitness-force/shared';
import { LostDealComponent } from '@fitness-force/crm';
import { MultiTenantListComponent } from '../multi-tenant-list/multi-tenant-list.component';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TextareaModule } from 'primeng/textarea';

interface Option {
  name: string;
  value: number;
}
interface Preference {
  name: string;
  code: string;
}

@Component({
  selector: 'create-client-profile-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    SelectModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    AvatarModule,
    ButtonModule,
    RippleModule,
    BlockUIModule,
    StyleClassModule,
    InputTextModule,
    InputMaskModule,
    DatePickerModule,
    MiniSearchComponent,
    ErrorMsgComponent,
    NgxFileDropModule,
    NgxIntlTelInputComponent,
    ToggleSwitchModule,
    DrawerModule,
    TranslateModule,
    AssignedToComponent,
    InputNumberModule,
    SkeletonModule,
    WalkInTourComponent,
    WalkInTrialComponent,
    CreateOpportunityComponent,
    LostDealComponent,
    MultiTenantListComponent,
    ToastModule,
    TextareaModule
  ],
  templateUrl: './prospect-form.component.html',
  styleUrls: ['./prospect-form.component.scss'],
  providers: [ DialogService, MessageService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProspectFormComponent implements OnInit, OnDestroy {
  @Input() showProspect = false;
  @Input() clientData: ClientById | null = null;
  @Input() formType: 'prospect' | 'walk-in-tour' | 'walk-in-trial' | 'walk-in-member' | 'contact' = 'prospect';
  @Input() formEvent: 'create' | 'update' = 'create';
  @Input() searchData?: { name?: string; email?: string; mobile?: string };
  @Input() contactData: any;
  @Output() hideProspect = new EventEmitter<number>();
  @Output() GoToClientDetails = new EventEmitter<{clientId:number}>();
  @Output() proceedToSignup = new EventEmitter<{proceesToSignup:boolean,Data:any}>();
  @Output() closeEditProfile = new EventEmitter();


  // Properties for subscription management
  private destroy$ = new Subject<void>();
  private isFormSubmitting$ = new BehaviorSubject<boolean>(false);
  private errorMessages$ = new BehaviorSubject<string[]>([]);

  // Form properties
  fg: UntypedFormGroup = new UntypedFormGroup({});
  minDate: Date = new Date();
  selectedTenant = 0;
  state_code = '';
  city_code = '';
  country_code = '';
  taskCategoryId = 0;
  stageMasterId = 0;
  showOtherSourceTextBox = false;
  isCapitalizing = false;
  modified = new EventEmitter<boolean>();
  errorMessages: string[] = [];

  // Phone number handling
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  onlyCountries: CountryISO[] = [];
  tenantCountry!: string;
  phoneUtil: any = lpn.PhoneNumberUtil.getInstance();

  // Collections
  EnumGender = Object.entries(ClientGender)
    .filter((key) => typeof key[1] != 'number')
    .map(([key, value]) => ({ key, value }));
  channelCollection = CONSTANTS_DATA.CHANNEL_SOURCES;
  promotionalPreferenceCollection :any[]= CONSTANTS_DATA.PREFERENCE.filter(o => o.code != 'Phone');
  


  // State management
  organizations: Option[] = [];
  showOtherGender = new BehaviorSubject<boolean>(true);
  clientId$ = new BehaviorSubject<number | null>(null);
  isClientRepresentative = new BehaviorSubject(true);
  claims: string[] = ['can_be_client_rep'];
  selectedClientRep = new BehaviorSubject(0);
  staffIds$ = new BehaviorSubject<number[]>([0]);
  showClientOrganizationLink = false;
  relationships = [
    { name: 'Parent', value: 11 },
    { name: 'Child', value: 12 },
    { name: 'Spouse', value: 13 },
    { name: 'Sibling', value: 14 }
  ];

  // Additional Information Properties
  showAdditionalInformation = false;
  selectedCountry: any;
  countries: any[] = [];
  clientById: any;
  promotionPreferenceMap = new Map();
  existClientForEmail: any;
  existClientForMobile: any;
  tenantDetails:any;
  planSummarydataObject$ = new BehaviorSubject<PlanSummary | null>(null);
  showPaidFullSidebar: boolean = false;
  userDetails: any;
  companyDetails :any;
  referredBy: number = 0;
  showSkeleton = true;
  refCropper?: DynamicDialogRef;
  @ViewChild('uploadClientImage') uploadClientImage: ElementRef;

  prospectData: any = {};
  uploadSuccess: boolean = false;
  uploadProfilePicture: boolean = false;
  imageBase64: string = '';
  existingImage: string = '';

  serviceTypes: any[] = [];
  showChooseReason: boolean = false;
  lostDealItemInfo: {tenant_id:number,id:number,client_info:any};

  emailAlreadyExists:boolean = false;
  mobileAlreadyExists:boolean = false;
  clientExistOnEmail:boolean = false;
  clientExistOnMobile:boolean = false;

  showClientSidebar:boolean = false;
  activeClientId:number = 0;
  clientMobileNoCountry:string = '';

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private clientService: ClientService,
    private opportunityStageService: OpportunityStageService,
    private taskCategory: TaskCategoryService,
    private organizationService: OrganizationService,
    private datePipe: DatePipe,
    public translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private authService: AuthSevices,
    private commonService: CommonService,
    private omnisearchService: OmniSearchService,
    private toastMessageService: ToastMessageService,
    private dialogService: DialogService,
    private timeService: TimeconversionService,
    private messageService: MessageService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    
  }


  ngOnInit(): void {
    this.initializeForm();
    this.setupSubscriptions();
    if (this.clientData) {
      this.GetClient(this.clientData.id);
    }
    else{
      this.setupPromotionalPreferences(null);
      setTimeout(() => {
        this.showSkeleton = false;
        this.cdr.detectChanges();
      }, 1000);
    }
  }

  GetClient(clientId: number) {
    this.clientService.getClientById(clientId)
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          this.handleError(error);
          return EMPTY;
        })
      )
      .subscribe({
        next: (client: any) => {
          this.clientById = client;
          this.referredBy = client.referred_by;
         
          if(client.mobile_country_code)
          {
            this.clientMobileNoCountry = this.countries.find(o => o.dial_code.replace(/\D/g, "") == client.mobile_country_code.replace(/\D/g, ""))!.two_letter_code.toLowerCase();
          }
          this.updateFormWithClientData(client);
          this.showSkeleton = false;
          this.cdr.detectChanges();

        },
        error: (error: any) => {
          this.handleError(error);
          this.showSkeleton = false;
          this.cdr.detectChanges();
        }
      });
  }

  // Update form with client data
  private updateFormWithClientData(client: ClientById): void {
    const profileData = {
      full_name: client.full_name,
      mobileNo: client.mobile_number && client.mobile_number > 0 ? this.getParsedNumber(String(client.mobile_number)) : '',
      selectedGender: this.EnumGender.find((g) => g.value == client.gender),
      emailId: client.email_id,
      channel: this.findChannel(client.channel),
      source: this.getSource(client),
      dateOfBirth: client.date_of_birth ? new Date(client.date_of_birth) : null,
      emergencyContact: client.emergency_contact_person,
      emergencyNumber: client.emergency_contact_number ? this.getParsedNumber(String(client.emergency_contact_number)) : null,
      medicalAlert: client.medical_alert,
      referedBy: client.referred_by,
      organisation: this.findOrganization(client.organisation),
      is_child: client.is_child,
      parent_client: client.parent_client_id,
      relation: this.relationships.find((c) => c.name == client.relation),
      promotionalPreference: this.getPromotionalPreferences(client),
      secondMobileNo: client.second_mobile_number ? this.getParsedNumber(String(client.second_mobile_number)) : null,
      preferredLanguage: client.preferred_language ? this.SetPrefersLanguage(client.preferred_language) : null,
      address_pin_code: client.address_pin_code,
      address: client.address,
      national_id: client.national_id,
      taxNumber: client.taxnumber,
      migrationId: client.migration_id,
      notes: client.note,
      newRepresentative: client.client_representative_id,
      referredBy: client.referred_by
    };

    this.fg.patchValue(profileData);
    this.fg.updateValueAndValidity();
    this.selectedClientRep.next(client.client_representative_id);
    this.cdr.detectChanges();
    this.setupPromotionalPreferences(client);
    this.showOtherSourceTextBox = this.isOtherChannel(client.channel);
    this.NewRepresentative.disable();
    if(client.image && client.image != '' && client.image != null)
    {
      this.uploadSuccess = true;
      this.existingImage = client.image;
    }
    this.cdr.detectChanges();
  }

  private initializeForm(): void {
    this.fg = this.fb.group({
      full_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      emailId: [''],
      mobileNo: [''],
      channel: ['', [Validators.required]],
      source: [{ value: '', disabled: true }, [Validators.required]],
      selectedGender: ['', [Validators.required]],
      referredBy: [0],  
      promotionalPreference: [[]],
      createTask: [this.formType === 'prospect'],
      createOpportunity: [false],
      createWalkInTour: [this.formType === 'walk-in-tour'],
      createWalkInTrial: [this.formType === 'walk-in-trial'],
      createWalkInMember: [this.formType === 'walk-in-member'],
      taskType: new UntypedFormControl(null, []),
      taskOn: new UntypedFormControl([], []),
      taskOnDate: new UntypedFormControl('', []),
      interestedIn: new UntypedFormControl({ value: [] as Option[], disabled: true }, [
        Validators.required
  ]),
      newRepresentative: ['', [Validators.required]],
      organisation: [null],
      dateOfBirth: [null],
      emergencyContact: [''],
      emergencyNumber: [''],
      medicalAlert: [''],
      is_child: [false],
      parent_client: [''],
      relation: [null],
      secondMobileNo: [''],
      preferredLanguage: [null],
      address_pin_code: [''],
      address: [''],
      national_id: [''],
      taxNumber: [''],
      migrationId: [''],
      notes: [''],
      email:[false],
      whatsapp:[false],
      sms:[false],
      image: ['']
    });

    if(this.formType === 'contact'){
      this.fg.addControl('tenant_id', new UntypedFormControl(null, [Validators.required]));
    }

    // Set search data if available
    if (this.searchData && Object.keys(this.searchData).length > 0) {
      if (this.searchData.name && this.searchData.name.trim()) {
        this.fg.patchValue({ full_name: this.searchData.name });
      }
      if (this.searchData.email && this.searchData.email.trim()) {
        this.fg.patchValue({ emailId: this.searchData.email });
      }
      if (this.searchData.mobile && this.searchData.mobile.trim()) {
        // Note: You might need to adjust this based on your phone input component's requirements
        if(this.formType === 'contact'){
          this.fg.patchValue({ mobileNo: this.getParsedNumber(String(this.searchData.mobile)) });
        }
        else{
          this.fg.patchValue({ mobileNo: this.searchData.mobile });
        }
      }
    }
    // Set initial values if needed
    if (!this.clientData) {
      this.setDefaultValues();
    }

    // Update validity of all controls
    Object.values(this.fg.controls).forEach(control => {
      control.updateValueAndValidity({ emitEvent: false });
    });

    this.fg.setValidators(this.emailMobileValidator());
    this.fg.updateValueAndValidity({ emitEvent: false });
  }

  private emailMobileValidator(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const emailControl = formGroup.get('emailId');
      const mobileControl = formGroup.get('mobileNo');

      if (!emailControl || !mobileControl) {
        return null;
      }

      const email = emailControl.value;
      const mobile = mobileControl.value;

      // Check if at least one field is filled and valid
      if ((email && !emailControl.errors?.['email']) || 
          (mobile && !mobileControl.errors?.['phoneNumber'])) {
        return null;
      }

      return { emailOrMobileRequired: true };
    };
  }

  private setDefaultValues(): void {
    const defaultValues = {
      selectedGender: this.showOtherGender.value ? 
        this.EnumGender.find(obj => obj.value === 'PreferNotToRespond') : null,
    };

    // Only set values that exist
    Object.entries(defaultValues).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        this.fg.get(key)?.setValue(value);
      }
    });
  }

  private setupFormValidation(): void {
    if (!this.fg) return; // Guard clause to prevent null access
    // Channel validation
    this.fg.get('channel')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode: any) => {
        if (mode?.channel) {
          this.fg.get('source')?.enable({ onlySelf: true });
          if (mode.channel.toLowerCase() === 'other') {
            this.fg.get('source')?.clearValidators();
            this.fg.get('source')?.setValue('');
            this.showOtherSourceTextBox = true;
          } else {
            this.fg.get('source')?.setValidators([Validators.required]);
            this.showOtherSourceTextBox = false;
          }
        } else {
          this.fg.get('source')?.clearValidators();
        }
        this.fg.get('source')?.updateValueAndValidity();
      });

      this.fg.get('is_child')?.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: any) => {
        if (value) {
          this.fg.get('parent_client')?.setValidators(Validators.required);
          this.fg.get('parent_client')?.updateValueAndValidity({ onlySelf: true });
          this.fg.get('relation')?.setValidators(Validators.required);
          this.fg.get('relation')?.updateValueAndValidity({ onlySelf: true });
        } else {
          this.fg.get('parent_client')?.clearValidators();
          this.fg.get('parent_client')?.setValue('');
          this.fg.get('parent_client')?.updateValueAndValidity({ onlySelf: true });
          this.fg.get('relation')?.clearValidators();
          this.fg.get('relation')?.setValue(null);
          this.fg.get('relation')?.updateValueAndValidity({ onlySelf: true });
        }
        this.fg.updateValueAndValidity();
        this.cdr.detectChanges();
      });

    // Email/Mobile cross validation
    this.setupEmailMobileValidation();
  }

  private setupEmailMobileValidation(): void {
    // Email validation based on mobile
    this.fg.get('emailId')?.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value: any) => {
        if (value && this.fg.get('emailId')?.valid) {
          this.fg.get('mobileNo')?.removeValidators([Validators.required]);
          if(this.mobileAlreadyExists){
            this.MobileNoControl.setErrors({mobileExists:true});
            this.cdr.detectChanges();
          }
        } else {
          this.fg.get('mobileNo')?.setValidators([Validators.required]);
          if(this.mobileAlreadyExists){
            this.MobileNoControl.setErrors({mobileExists:true});
            this.cdr.detectChanges();
          }
        }
        this.fg.get('mobileNo')?.updateValueAndValidity({ onlySelf: true });
      });

    // Mobile validation based on email
    this.fg.get('mobileNo')?.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value: any) => {
        if (value && this.fg.get('mobileNo')?.valid) {
          this.fg.get('emailId')?.removeValidators([Validators.required, Validators.email]);
          if(this.emailAlreadyExists){
            this.EmailIdControl.setErrors({emailExists:true});
            this.cdr.detectChanges();
          }
        } else {
          this.fg.get('emailId')?.setValidators([Validators.required, Validators.email]);
          if(this.emailAlreadyExists){
            this.EmailIdControl.setErrors({emailExists:true});
            this.cdr.detectChanges();
          }
        }
        this.fg.get('emailId')?.updateValueAndValidity({ onlySelf: true });
      });
  }

  get parseSupportedLang(): Array<LANGUAGE> {
    let supportedLangBuffer: Array<LANGUAGE> = [];
    const supportedLang = this.companyDetails.compDetails.supported_languages.map((l: any) =>
      l.trim().toLowerCase()
    );
    supportedLangBuffer = CONSTANTS_DATA.ALL_LANGUAGES.filter((lang) => {
      const languageOnlower = lang.LANG_ABBRE.toLowerCase();
      const langCodeOnlower = lang.LANG_CODE.toLowerCase();
      if (supportedLang && supportedLang.length > 0) {
        return (
          supportedLang &&
          (supportedLang.includes(languageOnlower) ||
            supportedLang.includes(langCodeOnlower))
        );
      } else if (
        this.companyDetails.compDetails.primary_language == languageOnlower
      ) {
        return lang;
      } else {
        return { COUNTRY: 'us', LANG_CODE: 'en', LANG_ABBRE: 'ENGLISH' };
      }
    });
    return supportedLangBuffer;
  }

  private setupEmailMobileExistenceCheck(): void {
    // Check if email exists
    this.fg.get('emailId')?.valueChanges
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap(email => {
          const isExistingClient = this.clientData ? this.clientData?.email_id == email : false;
          if (email && this.EmailIdControl.valid && !this.EmailIdControl.errors?.['email'] && !isExistingClient) {
            return this.checkClientExist(email);
          }
          return of(null);
        })
      )
      .subscribe(clients => {
        if (clients && clients.length > 0 && clients.find((c: { clt_emailid: any; }) => c.clt_emailid === this.EmailIdControl?.value)) {
          if(this.LinkedClientIdControl.value != 0 && this.LinkedClientIdControl.value !='' && this.LinkedClientIdControl.value == clients.find((c: { clt_emailid: any; }) => c.clt_emailid === this.EmailIdControl?.value).id)
          {
            const currentErrors = this.EmailIdControl?.errors ? { ...this.EmailIdControl.errors } : {};
            delete currentErrors['emailExists'];
            this.EmailIdControl?.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
            this.existClientForEmail = null;
            this.emailAlreadyExists = false;
            this.clientExistOnEmail=false;
            this.cdr.detectChanges();
          }
          else
          {
            // Set error and store existing client
            this.EmailIdControl?.setErrors({ emailExists: true });
            this.EmailIdControl?.updateValueAndValidity();
            this.EmailIdControl?.markAsTouched();
            this.existClientForEmail = clients.find((c: { clt_emailid: any; }) => c.clt_emailid === this.EmailIdControl?.value);
            this.emailAlreadyExists = true;
            this.clientExistOnEmail=true;
          }
          this.cdr.detectChanges();
        } else {
          // Clear emailExists error while preserving other errors
          const currentErrors = this.EmailIdControl?.errors ? { ...this.EmailIdControl.errors } : {};
          delete currentErrors['emailExists'];
          this.EmailIdControl?.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
          this.EmailIdControl?.updateValueAndValidity();
          this.existClientForEmail = null;
          this.emailAlreadyExists = false;
          this.clientExistOnEmail=false;
          this.cdr.detectChanges();
        }
      });

    // Check if mobile number exists
    this.fg.get('mobileNo')?.valueChanges
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap(mobileNo => {
          // Handle both new object format and string format
          const parsedNumber = mobileNo ? 
            (typeof mobileNo === 'object' && mobileNo.number ? 
              String(mobileNo.number).replace(/\s+/g, '').trim() : 
              String(mobileNo).replace(/\s+/g, '').trim()) : '';
          
          const isExistingClient = this.clientData ? 
            (this.clientData?.mobile_number?.toString() == parsedNumber) : false;
            
          if (mobileNo && this.MobileNoControl?.valid && !isExistingClient) {
            return this.checkClientExist(parsedNumber);
          }
          return of(null);
        })
      )
      .subscribe(clients => {
        const mobileControl = this.fg.get('mobileNo');
        // Handle both new object format and string format
        const parsedNumber = mobileControl?.value ? 
          (typeof mobileControl.value === 'object' && mobileControl.value.number ? 
            String(mobileControl.value.number).replace(/\s+/g, '').trim() : 
            String(mobileControl.value).replace(/\s+/g, '').trim()) : '';
          
        if (clients && clients.length > 0 && clients.find((c: { clt_mobile_no: any; }) => c.clt_mobile_no.toString() === parsedNumber.toString())) {
          if(this.LinkedClientIdControl.value != 0 && this.LinkedClientIdControl.value !='' && this.LinkedClientIdControl.value == clients.find((c: { clt_mobile_no: any; }) => c.clt_mobile_no.toString() === parsedNumber.toString()).id)
          {
            // Clear mobileExists error while preserving other errors
            const currentErrors = this.MobileNoControl?.errors ? { ...this.MobileNoControl.errors } : {};
            delete currentErrors['mobileExists'];
            this.MobileNoControl?.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
            this.existClientForMobile = null;
            this.mobileAlreadyExists = false;
            this.clientExistOnMobile=false;
          }
          else{
            // Set error and store existing client
            this.MobileNoControl?.setErrors({ mobileExists: true });
            this.MobileNoControl?.markAsTouched();
            this.existClientForMobile = clients.find((c: { clt_mobile_no: any; }) => c.clt_mobile_no.toString() === parsedNumber.toString());
            this.mobileAlreadyExists = true;
            this.clientExistOnMobile=true;
          }
        } else {
          // Clear mobileExists error while preserving other errors
          const currentErrors = this.MobileNoControl?.errors ? { ...this.MobileNoControl.errors } : {};
          delete currentErrors['mobileExists'];
          this.MobileNoControl?.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
          this.existClientForMobile = null;
          this.mobileAlreadyExists = false;
          this.clientExistOnMobile=false;
        }
        this.cdr.detectChanges();
      });

  }

  private checkClientExist(query: string): Observable<any> {
    if (!query) {
      return of(null);
    }
    
    const request = {
      language: this.locale,
      query: query,
      providers: [
        {
          provider_name: ElasticProviderName.Clients,
          pagination: { page_number: 0, page_size: 10 },
        }
  ],
    };

    return this.omnisearchService.Search(request).pipe(
      map(response => response.searchResults.clients.items),
      catchError(() => of(null))
    );
  }

  
  formFieldChanges()
  {
    // Add form control value change subscriptions
    this.fg.controls.channel.valueChanges.subscribe(
      (mode: any) => {
        if (mode && mode.channel) {
          this.fg.controls.source.setValue('');
          if (mode.channel.toLowerCase() === 'other') {
            this.fg.controls.source.clearValidators();
            this.showOtherSourceTextBox = true;
          }
          else {
            this.fg.controls.source.setValidators([Validators.required]);
            this.showOtherSourceTextBox = false;
          }
        } else {
          this.fg.controls.source.clearValidators();
        }
        this.fg.controls.source.updateValueAndValidity();
      }
    );

    this.fg.get('emailId')?.valueChanges.subscribe((value: any) => {
      if (value && this.EmailIdControl?.valid) {
        this.EmailIdControl?.removeValidators([Validators.required, Validators.email]);
        if(this.emailAlreadyExists){
          this.EmailIdControl.setErrors({emailExists:true});
          this.cdr.detectChanges();
        }
        this.EmailIdControl?.updateValueAndValidity({ onlySelf: true });
      }
      else {
        this.MobileNoControl?.setValidators([Validators.required, Validators.email]);
        if(this.mobileAlreadyExists){
          this.MobileNoControl.setErrors({mobileExists:true});
          this.cdr.detectChanges();
        }
        this.MobileNoControl?.updateValueAndValidity({ onlySelf: true });
      }
    });

    this.fg.get('mobileNo')?.valueChanges.subscribe((value: any) => {
      if (value && this.MobileNoControl?.valid) {
        this.EmailIdControl?.removeValidators([Validators.required, Validators.email]);
        if(this.emailAlreadyExists){
          this.EmailIdControl.setErrors({emailExists:true});
          this.cdr.detectChanges();
        }
        this.EmailIdControl?.updateValueAndValidity({ onlySelf: true });
      }
      else {
        this.EmailIdControl?.setValidators([Validators.required, Validators.email]);
        if(this.emailAlreadyExists){
          this.EmailIdControl.setErrors({emailExists:true});
          this.cdr.detectChanges();
        }
        this.EmailIdControl?.updateValueAndValidity({ onlySelf: true });
      }
    });

   
  }

  private setupSubscriptions(): void {
    // Company info subscription
    this.store.select(COMPANY_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe(responseData => {
        this.companyDetails = responseData;
        this.translate.setDefaultLang(responseData?.selectedLang?.LANG_CODE);
        this.translate.use(responseData?.selectedLang?.LANG_CODE);
      });

    // Tenant info subscription
    this.store.select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe(tenant => {
        this.selectedTenant = tenant.selected_tenant.id;
        this.tenantDetails = tenant.selected_tenant;
        this.country_code = tenant.selected_tenant.country;
        this.state_code = tenant.selected_tenant.state;
        this.city_code = tenant.selected_tenant.city;
        this.loadInitialData(tenant.selected_tenant);
      });

      this.store.select(STAFF_STATE).pipe(takeUntil(this.destroy$))
        .subscribe((data: STATE_STAFF_LIST) => {
          if (data && data.staffList && data.staffList.length > 0) {
            this.userDetails = data.staffList.find(
            (o: { id: number }) => o.id == this.authService.loggedInStaffId
          );
          const staffIds: number[] = [];
          data.staffList.forEach(s => {
            if (s.role == Roles.Admin || s.role.toString() == Roles[Roles.Admin]) {
              staffIds.push(s.id)
            }
            else if (s.role == Roles.Staff || s.role.toString() == Roles[Roles.Staff]) {
              if (s.can_be_client_rep) staffIds.push(s.id);
            }
          });
          this.staffIds$.next(staffIds);
          if (this.userDetails?.can_be_client_rep) {
            this.NewRepresentative.setValue(this.userDetails.id);
            this.selectedClientRep.next(this.userDetails.id);
          }
        }
      });

      this.store.select(STATSIG_STATE).pipe(takeUntil(this.destroy$)).subscribe({
        next: (statsigUser: any) => {
          if (statsigUser) {
            if (Statsig.checkGate("client_organisation_link")) {
              this.showClientOrganizationLink = true;
            }
            if (Statsig.checkGate("hide_additional_gender")) {
              this.showOtherGender.next(false);
            }
          }
        }
      });

      this.store.select(SERVICETYPE_LIST_STATE).pipe(takeUntil(this.destroy$)).subscribe({
        next: (serviceTypes: any) => {
          this.serviceTypes = serviceTypes;
        }
      });
    // Form control subscriptions
    this.setupFormValidation();
    this.setupEmailMobileExistenceCheck();
  }

  private loadInitialData(tenant: any): void {
    const tenantId = tenant.id;
    
    // Use forkJoin to make parallel requests
    forkJoin({
      organizations: this.organizationService.ListOrganization(tenantId),
      taskCategories: this.taskCategory.getTaskCategory(tenantId),
      opportunityStages: this.opportunityStageService.OpportunityStageMasterListService(
        tenantId, 
        OpportunityBoardType.NewMemberBoard
      )
    })
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          this.handleError(error);
          return EMPTY;
        })
      )
      .subscribe({
        next: (results) => {
          this.handleOrganizations(results.organizations);
          this.handleTaskCategories(results.taskCategories);
          this.handleOpportunityStages(results.opportunityStages);
          this.showSkeleton = false;
        }
      });

    // Handle country data separately as it comes from store
    this.GetCountry(tenant.country);
  }

  valueChangeFunction() {
    this.EmailIdControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: any) => {
        if (value && this.EmailIdControl.valid) {
          this.MobileNoControl.clearValidators();
        } else {
          this.MobileNoControl.setValidators([Validators.required]);
        }
        this.MobileNoControl.updateValueAndValidity({ onlySelf: true });
      });

    this.MobileNoControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: any) => {
        if (value && this.MobileNoControl.valid) {
          this.EmailIdControl.clearValidators();
        } else {
          this.EmailIdControl.setValidators([Validators.required, Validators.email]);
        }
        this.EmailIdControl.updateValueAndValidity({ onlySelf: true });
      });
  }

  GetOrganizations(tenantId: number): void {
    this.organizationService.ListOrganization(tenantId).subscribe({
      next: (organizations: any) => {
        if (organizations.items) {
          this.organizations = organizations.items.map((organization: any) => ({
            name: organization.name_primary_language,
            value: organization.id
          }));
        }
      },
      error: (error: any) => {
        this.toastMessageService.showError(error);
      }
    });
  }

  getTaskCategory(tenantId: number): void {
    this.taskCategory.getTaskCategory(tenantId).subscribe({
      next: (taskCategories: any) => {
        if (taskCategories?.items?.length > 0) {
          const salesCategory = taskCategories.items.find(
            (obj: any) => String(obj.name_primary_language).toLowerCase() === 'sales'
          );
          if (salesCategory) {
            this.taskCategoryId = salesCategory.id;
            this.modified.emit(true);
          }
        }
      },
      error: (error: any) => {
        this.toastMessageService.showError(error);
      }
    });
  }

  GetOpportunityStageMaster(tenantId: number): void {
    this.opportunityStageService
      .OpportunityStageMasterListService(tenantId, OpportunityBoardType.NewMemberBoard)
      .subscribe({
        next: (obj: any) => {
          if (obj?.items?.length > 0) {
            const leadOpps = obj.items.find(
              (o: any) => o.stage_master_key === OpportunityStageForNonMember.NewLeads
            );
            if (leadOpps?.id > 0) {
              this.stageMasterId = leadOpps.id;
            }
          }
        },
        error: (error: any) => {
          this.toastMessageService.showError(error);
        }
      });
  }

  GetCountry(countryCode: string): void {
    this.store.select(COUNTRY_MASTER_STATE_LIST).subscribe((country) => {
      if (country.length > 0) {
        this.countries = country;
        const foundCountry = country.find(o => 
          o.three_letter_code.toLowerCase() === countryCode.toLowerCase()
        );
        if (foundCountry) {
          this.tenantCountry = foundCountry.two_letter_code.toLowerCase();
          this.onlyCountries = Object.values(CountryISO);
        }
        if(this.clientById && this.clientById.mobile_country_code)
        {
          this.clientMobileNoCountry = country.find(o => o.dial_code.replace(/\D/g, "") == this.clientById.mobile_country_code.replace(/\D/g, ""))!.two_letter_code.toLowerCase();
        }
        if(this.searchData && this.searchData.mobile)
        {
          const phoneUtil = lpn.PhoneNumberUtil.getInstance();
          const number = phoneUtil.parse(this.searchData.mobile);
          this.clientMobileNoCountry = country.find(o => o.dial_code.replace(/\D/g, "") == number.getCountryCode()!.toString())!.two_letter_code.toLowerCase();
        }
      } else {
        this.countries = [];
      }

    });
  }

  SetGender(gender: any): void {
    this.fg.controls.selectedGender.setValue(gender);
  }

  SetGenderOnChange($event: any): void {
    this.SetGender($event.value);
  }

  GetSelectedGenderFromForm(): string {
    let selectedGender = ClientGender[ClientGender.PreferNotToRespond].toString();
    const gender = this.fg.get('selectedGender')?.value;
    switch (String(gender.value).toLowerCase()) {
      case 'male':
        selectedGender = ClientGender[ClientGender.Male].toString();
        break;
      case 'female':
        selectedGender = ClientGender[ClientGender.Female].toString();
        break;
      case 'nonbinaryornonconforming':
        selectedGender = ClientGender[ClientGender.NonBinaryOrNonConforming].toString();
        break;
      case 'prefernottorespond':
        selectedGender = ClientGender[ClientGender.PreferNotToRespond].toString();
        break;
      case 'transgender':
        selectedGender = ClientGender[ClientGender.Transgender].toString();
        break;
      default:
        break;
    }
    return selectedGender;
  }

  PromotionalPreferenceToTrue(notificationChannel: any) {
      if (this.promotionPreferenceMap.has(notificationChannel.name)) {
        this.promotionPreferenceMap.delete(notificationChannel.name);
      } else {
        this.promotionPreferenceMap.set(notificationChannel.name, notificationChannel);
      }
      this.PromotionalPreferenceControl.setValue(Array.from(this.promotionPreferenceMap.values()).map((o: { code: any; }) => o.code));
      console.log(Array.from(this.promotionPreferenceMap.values()).map((o: { code: any; }) => o.code));
  }



  IsPromotionOptionSelected(value: Preference): boolean {
    return this.fg.controls.promotionalPreference.value.some((o: Preference) => o.name === value.name);
  }

  capitalizeInput() {
    if (this.isCapitalizing) return;
    this.isCapitalizing = true;
    const nameControl = this.NameControl;
    if (nameControl.value) {
      const capitalizedValue = nameControl.value.charAt(0).toUpperCase() + nameControl.value.slice(1);
      nameControl.setValue(capitalizedValue, { emitEvent: false });
    }
    this.isCapitalizing = false;
  }

  CreateProspectSubmit() {
    if (this.fg.valid) {
      this.isFormSubmitting$.next(true);
      let request$: Observable<any>;
      if(this.clientData){
        request$ = this.UpdateClient(this.SetClientUpdateObject());
      }else{
        request$ = this.CreateProspect(this.SetProspectObject());
      }
      request$
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.isFormSubmitting$.next(false))
        )
        .subscribe({
          next: (response: any) => this.handleSubmitSuccess(response),
          error: (error) => {
            this.handleError(error);
            this.isFormSubmitting$.next(false);
            this.cdr.detectChanges();
          }
        });
    } else {
      this.markFormGroupTouched(this.fg);
      this.cdr.detectChanges();
    }
  }

  private UpdateClient(payload: any): Observable<any> {
    const clientDataObject = this.GetClientPatchObject(payload, this.clientById);
    if(clientDataObject && clientDataObject.length > 0)
    {
      return this.clientService.patchClient(this.clientById.id, this.selectedTenant, clientDataObject)

      .pipe(
        tap(() => {
          this.translate
            .get('EDIT_CLIENT_PROFILE.MSG_UPDATE')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
        }),
        delay(500)
      );
    }
    else
    {
      return of(null);
    }
  }

  private CreateProspect(payload: CreateProspectRequest): Observable<any> {
    let service: any;
    if(this.formType === 'contact')
    {
      service = this.clientService.createProspectFromContact(this.TenantIdControl.value,this.contactData.contact_id,payload);
    }
    else{
      service = this.clientService.createProspect(payload);
    }
    return service.pipe(
        tap((response: CreateProspectResponse) => {
          this.clientId$.next(response.client_id);
        this.commonService.addProspect(true);
        this.translate
          .get('Shared.CREATE-PROSPECT.CLIENT_SUCCESS')
          .subscribe((translation) => {
           const data = {
          clientName: this.NameControl.value,
          clientId: response.client_id,
        };
        this.messageService.add({severity:'success', key: 'client-toast', summary: 'Success', detail: translation, data: data});
        //this.toastMessageService.showSuccess(translation);
        });
        this.cdr.detectChanges();
      }),
        delay(1000)
      );
  }

  private async handleSubmitSuccess(response: any) {
    this.clientId$.next(response.client_id || response.id);
    if(this.formEvent == 'create' && this.formType == 'prospect'){
      this.commonService.addProspect(true);
      await this.timeout(4000);
      this.isFormSubmitting$.next(false);
      this.closeSidebar();
    }
    else if(this.formEvent == 'create' && this.formType == 'walk-in-member'){
      this.isFormSubmitting$.next(false);
      this.proceedToSignup.emit({proceesToSignup:true,Data:this.setSignupData(response)});
      this.closeSidebar();
    }
    else if(this.formEvent == 'create' && this.formType == 'walk-in-tour'){
      if(this.fg.get('tour_completed')?.value?.tour_outcome.shortname === 'signup'){
        this.isFormSubmitting$.next(false);
        this.commonService.addProspect(true);
        this.proceedToSignup.emit({proceesToSignup:true,Data:this.setSignupData(response)});
        this.closeSidebar();
      }
      else if(this.fg.get('tour_completed')?.value?.tour_outcome.shortname === 'notintrested'){
        this.isFormSubmitting$.next(false);
        this.lostDealItemInfo = {tenant_id:this.selectedTenant,id:response.opportunity_id,client_info:{id:response.client_id,full_name:this.NameControl.value,status:'Prospect',image:'https://www.google.com/images/branding/googlelogo/1x/googlelogo_color_272x92dp.png'}};
        this.showChooseReason = true;
        this.commonService.addProspect(true);
      }
    }
    else if(this.formEvent == 'update'){
      this.isFormSubmitting$.next(false);
      this.closeSidebar(true);
    }
    else if(this.formEvent == 'create' && this.formType == 'contact'){
      this.isFormSubmitting$.next(false);
      this.closeSidebar(true);
    }
  }

  setSignupData(response: CreateProspectResponse){
    const planSummary: PlanSummary = {
      id: 0,
      from_subscription_id:0,
      client: {
        client_id: response.client_id,
        client_name: this.NameControl.value,
        client_status: 'Prospect',
      } as PlanSummaryClient,
      couponcode: null,
      tenant_id: this.selectedTenant,
      company_id: this.tenantDetails.company_id,
      startdate: new Date(),
      plan_selectable: true,
      CRM: {
        opp_id: 0,
        task_id: response.task_id,
        board_id: OpportunityBoardType.NewMemberBoard,
      } as PlanSummaryCRM,
      Scheduling: null,
    } as PlanSummary;
    return planSummary;
  }

  private handleError(error: any) {
    this.toastMessageService.showError(error);
  }


  private handleOrganizations(response: any): void {
    if (response?.items) {
      this.organizations = response.items.map((organization: any) => ({
        name: organization.name_primary_language,
        value: organization.id
      }));
    }
  }

  private handleTaskCategories(response: any): void {
    if (response?.items?.length > 0) {
      const salesCategory = response.items.find(
        (obj: any) => String(obj.name_primary_language).toLowerCase() === 'sales'
      );
      if (salesCategory) {
        this.taskCategoryId = salesCategory.id;
      }
    }
  }

  private handleOpportunityStages(response: any): void {
    if (response?.items?.length > 0) {
      const leadOpps = response.items.find(
        (o: any) => o.stage_master_key === OpportunityStageForNonMember.NewLeads
      );
      if (leadOpps?.id > 0) {
        this.stageMasterId = leadOpps.id;
      }
    }
  }

  GetClientPatchObject(updateProfileData: any, clientdata: any): Array<{ op: string, path: string, value: any }> {
    const clientObj: Array<{ op: string, path: string, value: any }> = [];
    Object.entries(updateProfileData).forEach(obj => {
      if (this.isValueChanged(obj, clientdata)) {
        switch (obj[0]) {
          case 'full_name':
            clientObj.push({ op: "replace", path: "/FullName", value: obj[1] });
            break;
          case 'date_of_birth':
            clientObj.push({ op: "replace", path: "/DateOfBirth", value: obj[1] });
            break;
          case 'gender':
            clientObj.push({ op: "replace", path: "/Gender", value: obj[1] });
            break;
          case 'email_id':
            clientObj.push({ op: "replace", path: "/EmailId", value: obj[1] });
            break;
          case 'mobile_number':
            clientObj.push({ op: "replace", path: "/MobileNumber", value: obj[1] });
            break;
          case 'mobile_country_code':
            clientObj.push({ op: "replace", path: "/MobileCountryCode", value: obj[1] });
            break;
          case 'source':
            clientObj.push({ op: "replace", path: "/Source", value: obj[1] });
            break;
          case 'channel':
            clientObj.push({ op: "replace", path: "/Channel", value: obj[1] });
            break;
          case 'emergency_contact_number':
            clientObj.push({ op: "replace", path: "/EmergencyContactNumber", value: obj[1] });
            break;
          case 'emergency_contact_person':
            clientObj.push({ op: "replace", path: "/EmergencyContactPerson", value: obj[1] });
            break;
          case 'medical_alert':
            clientObj.push({ op: "replace", path: "/MedicalAlert", value: obj[1] });
            break;
          case 'organisation':
            clientObj.push({ op: "replace", path: "/OrganisationId", value: obj[1] });
            break;
          case 'promotional_notification_channels_allowed':
            clientObj.push({ op: "replace", path: "/PromotionalNotificationChannelsAllowed", value: obj[1] && (obj[1] as string[]).length > 0 ? obj[1] : ['None'] });
            break;
          case "is_child":
            clientObj.push({ op: "replace", path: "/IsChild", value: obj[1] });
            break;
          case "parent_client_id":
            clientObj.push({ op: "replace", path: "/ParentClientId", value: obj[1] });
            break;
          case "relation":
            clientObj.push({ op: "replace", path: "/Relation", value: obj[1] });
            break;
          case "image":
            clientObj.push({ op: "replace", path: "/ImageBase64", value: obj[1] });
            break;
          case "taxnumber":
            clientObj.push({ op: "replace", path: "/TaxNumber", value: obj[1] });
            break;
          case "migration_id":
            clientObj.push({ op: "replace", path: "/MigrationId", value: obj[1] });
            break;
          case "note":
            clientObj.push({ op: "replace", path: "/Notes", value: obj[1] });
            break;
          case "national_id":
            clientObj.push({ op: "replace", path: "/NationalId", value: obj[1] });
            break;
          case "medical_alert":
            clientObj.push({ op: "replace", path: "/MedicalAlert", value: obj[1] });
            break;
          case "address":
            clientObj.push({ op: "replace", path: "/Address", value: obj[1] });
            break;
          case "address_pin_code":
            clientObj.push({ op: "replace", path: "/AddressPinCode", value: obj[1] });
            break;
          case "place_id_request":
            clientObj.push({ op: "replace", path: "/PlaceIdRequest", value: obj[1] });
            break;
          default:
            break;
        }
      }
    });
    return clientObj;
  }

  isValueChanged(obj: any, clientdata: any) {
    let isChanged: boolean =  false;
    Object.entries(clientdata)
    const found = Object.entries(clientdata)
      .find(pair => pair[0] === obj[0]);

    if (found) {
      if(found[0] == 'promotional_notification_channels_allowed') {
        isChanged = JSON.stringify(found[1]) !== JSON.stringify(obj[1]);
      }
      else if (found[0] == 'organisation') {
        if(found[1] == null && obj[1] != null)
        {
          isChanged = true;
        }
        else if(found[1] != null && (found[1] as any).id !== obj[1]){
          isChanged = true;
        }
        else{
          isChanged = false;
        }
      }
      else if (found[1] != obj[1]) {
        isChanged = true;
      }
    }
    return isChanged;
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private getParsedNumber(phoneNumber: string): any {
    if (!phoneNumber) return '';
    
    let number: lpn.PhoneNumber = '' as unknown as lpn.PhoneNumber;
    let num: any = 0;
    try {
      // Get phone util instance
      const phoneUtil = lpn.PhoneNumberUtil.getInstance();
      
      // Parse number differently if it already has a plus sign (has dial code)
      if (phoneNumber.startsWith('+')) {
        number = phoneUtil.parse(phoneNumber);
      } else {
        number = phoneUtil.parse(phoneNumber,this.clientMobileNoCountry ? this.clientMobileNoCountry.toUpperCase() : this.tenantCountry.toUpperCase());
      }
      
      num = phoneUtil.format(number, lpn.PhoneNumberFormat.NATIONAL);
    } 
    catch (e) {
      console.error("Error parsing phone number:", e);
    }
    return num;
  }

  SetClientUpdateObject() {
    const baseData = {
      full_name: this.fg.get('full_name')?.value,
      mobile_country_code: this.fg.get('mobileNo')?.value?.dialCode || '',
      mobile_number: this.fg.get('mobileNo')?.value?.number?.replace(/[\s\-\(\)]/g, '') || 0,
      email_id: this.fg.get('emailId')?.value,
      date_of_birth: this.fg.get('dateOfBirth')?.value ? this.datePipe.transform(this.fg.get('dateOfBirth')?.value, 'yyyy-MM-dd') : null,
      gender: this.GetSelectedGenderFromForm(),
      second_mobile_country_code: this.fg.get('secondMobileNo')?.value?.dialCode || null,
      second_mobile_number: this.fg.get('secondMobileNo')?.value?.number?.replace(/[\s\-\(\)]/g, '') || null,
      preferred_language: this.fg.get('preferredLanguage')?.value?.LANG_CODE || '',
      address: this.fg.get('address')?.value,
      address_pin_code: this.fg.get('address_pin_code')?.value,
      emergency_contact_number: this.fg.get('emergencyNumber')?.value?.e164Number || null,
      emergency_contact_person: this.fg.get('emergencyContact')?.value,
      source: this.fg.get('source')?.value || 'Other',
      channel: this.fg.get('channel')?.value?.channel,
      referred_by: this.fg.get('referredBy')?.value || 0,
      medical_alert: this.fg.get('medicalAlert')?.value,
      note: this.fg.get('notes')?.value,
      taxnumber: this.fg.get('taxNumber')?.value,
      migration_id: this.fg.get('migrationId')?.value,
      organisation: this.fg.get('organisation')?.value?.value || 0,
      national_id: this.fg.get('national_id')?.value,
      promotional_notification_channels_allowed:  Array.from(this.promotionPreferenceMap.values()).map((o: { code: any; }) => o.code),
      is_child: this.fg.get('is_child')?.value,
      parent_client_id: this.fg.get('is_child')?.value ? this.fg.get('parent_client')?.value : 0,
      relation: this.fg.get('is_child')?.value ? this.fg.get('relation')?.value?.value : ClientRelation[ClientRelation.None],
      createdin_tenant_id: this.selectedTenant,
      home_club_tenant_id: this.selectedTenant,
      client_representative_id: this.fg.get('newRepresentative')?.value ? this.fg.get('newRepresentative')?.value.id : 0,
      country_code: this.country_code,
      state_code: this.state_code,
      city_code: this.city_code,
      image: this.clientPicture_Base64 ? this.clientPicture_Base64 : this.clientById.image
    };
    return baseData;
  }

  SetProspectObject(): CreateProspectRequest {
    const client: CreateProspectRequest = {} as CreateProspectRequest;
    client.prospect = {
      full_name: this.NameControl.value,
      mobile_country_code: this.MobileNoControl.value
        ? this.MobileNoControl.value.dialCode
        : '',
      mobile_number: this.MobileNoControl.value
        ? this.MobileNoControl.value.number.replace(/[\s\-\(\)]/g, '')
        : 0,
      email_id: this.EmailIdControl.value,
      date_of_birth: this.DateOfBirthControl.value ? 
        this.datePipe.transform(this.DateOfBirthControl.value, 'yyyy-MM-dd') : null,
      gender: this.GetSelectedGenderFromForm(),
      location: '',
      second_mobile_country_code: this.SecondMobileNoControl.value ? this.SecondMobileNoControl.value.dialCode : '',
      second_mobile_number: this.SecondMobileNoControl.value ? this.SecondMobileNoControl.value.number.replace(/[\s\-\(\)]/g, '') : null,
      preferred_language: this.PreferredLanguageControl.value?.LANG_CODE || '',
      place_id: null,
      address: this.AddressControl.value || '',
      state_code: this.state_code || '',
      city_code: this.city_code || '',
      country_code: this.country_code || '',
      address_pin_code: this.AddressPinCodeControl.value || 0,
      emergency_contact_number: this.EmergencyNumberControl.value? this.EmergencyNumberControl.value?.e164Number || '' : '',
      emergency_contact_person: this.EmergencyContactControl.value || '',
      source: this.SourceControl.value,
      channel: this.ChannelControl.value.channel,
      heartrate_monitoring_id: null,
      referred_by: this.SelectedReferredBy.value ? String(this.SelectedReferredBy.value) : "",
      medical_alert: this.MedicalAlertControl.value || '',
      note: this.NotesControl.value || '',
      taxnumber: this.TaxNumberControl.value || '',
      custom_fields: null,
      current_keyfob: null,
      client_representative_id: this.NewRepresentative.value.id,
      createdin_tenant_id: this.selectedTenant,
      place_id_request: null,
      migration_id: this.MigrationIdControl.value || '',
      image_base64: this.clientPicture_Base64,
      password: '',
      organisation_id: this.organizationFormControl && this.organizationFormControl.value ? this.organizationFormControl.value.value : 0,
      promotional_notification_channels_allowed:  Array.from(this.promotionPreferenceMap.values()).map((o: { code: any; }) => o.code),
      is_child: this.IsChildClientFormControl.value,
      parent_client_id: this.IsChildClientFormControl.value ? this.LinkedClientIdControl.value : 0,
      relation: this.IsChildClientFormControl.value ? this.RelationshipControl.value.value : ClientRelation.None
    } as CreateClientRequest;

    if (this.CreateTask) {  
      if(this.CreateOpportunity )
      {
       client.opportunity = {
        name: client.prospect.full_name,
        stage_master_id: this.stageMasterId,
        service_type_ids: this.InterestedInIds.length > 0 ? this.InterestedInIds : this.serviceTypes.filter((serviceType: any) => serviceType.behaviour.toLowerCase() =='membership').map((serviceType: any) => serviceType.id),
        value_in_minor_currency: 0,
        expected_closure_date: this.TaskOnControl.value.value.toLowerCase() === 'custom date' ?  this.timeService.ConvertLocalDateTimeToUTC(this.TaskOnDateControl.value) : this.TaskOnControl.value.date(), // set null after venky API update
        assigned_to_staff_id: client.prospect.client_representative_id,
        comments: '',
        hot_warm_cold: OpportunitiesType.Hot,
      } as CreateOpportunityOnClientRequest;
    }

      client.task = {
        assigned_to_type: TaskAssignToType.ClientRep,
        assigned_to_staff_id: null,
        type: this.TaskTypeControl.value.name,
        category_id: this.taskCategoryId,
        title: client.prospect.full_name,
        notes: this.Notes || 'Task created at the time of prospect creation',
        priority: TaskPriority.High,
        call_id: null,
        email_id: null,
        sms_id: null,
        scheduled_date: this.timeService.ConvertLocalDateTimeToUTC(this.TaskOnControl.value.value.toLowerCase() === 'custom date' ?  this.TaskOnDateControl.value : this.TaskOnControl.value.date())
      } as CreateTaskByOpportunityOnClientRequest;
    }
    if(this.formType === 'walk-in-tour' && this.CreateTour)
    {
      const rowValues = this.fg.get('tour_completed')?.value;
      client.tour_completed = {
        outcome: rowValues.tour_outcome.value,
        scheduled_datetime: this.timeService.ConvertLocalDateTimeToUTC(rowValues.scheduled_datetime),
        assigned_to_staff_id: rowValues.assigned_to_staff_id ? rowValues.assigned_to_staff_id.id : 0,
        notes: rowValues.notes
      }
      if(rowValues.tour_outcome.shortname === 'booktrial' &&this.fg.contains('trial_membership'))
      {
        if(this.fg.get('selected_trial_type') && this.fg.get('selected_trial_type')?.value === 'membership')
        {
          const trialValues = this.fg.get('trial_membership')?.value;
          client.trial_membership = {
            trial_start_date: this.timeService.ConvertLocalDateTimeToUTC(trialValues.trial_start_date),
            notes: trialValues.notes,
            trial_preferred_time: this.timeService.GetUTCTimeFromDate(trialValues.trial_preferred_time),
            trial_owner_staff_id: trialValues.trial_owner_staff_id || 0
          }
          client.trial_classes = null;
        }
        else if(this.fg.get('selected_trial_type') && this.fg.get('selected_trial_type')?.value === 'class')
        {
          const trialValues = this.fg.get('trial_classes')?.value;
          client.trial_classes = {trial_classes:trialValues};
          client.trial_membership = null;
        }
        else{
          client.trial_classes = null;  
          client.trial_membership = null;
        }
      }
      else if(rowValues.tour_outcome.shortname === 'notintrested')
      {
        client.opportunity = {
          name: client.prospect.full_name,
          stage_master_id: this.stageMasterId,
          service_type_ids: this.serviceTypes.filter((serviceType: any) => serviceType.behaviour.toLowerCase() =='membership').map((serviceType: any) => serviceType.id),
          value_in_minor_currency: 0,
          expected_closure_date: this.TaskOnControl.value.value.toLowerCase() === 'custom date' ?  this.timeService.ConvertLocalDateTimeToUTC(this.TaskOnDateControl.value) : this.TaskOnControl.value.date(), // set null after venky API update
          assigned_to_staff_id: client.prospect.client_representative_id,
          comments: rowValues.notes,
          hot_warm_cold: OpportunitiesType.Cold,
        } as CreateOpportunityOnClientRequest;
        client.trial_classes = null;  
        client.trial_membership = null;
      }

      else{
        client.trial_classes = null;  
        client.trial_membership = null;
      } 

    }

    if(this.formType === 'walk-in-trial' && this.CreateTrial)
    {
        if(this.fg.get('selected_trial_type') && this.fg.get('selected_trial_type')?.value === 'membership')
        {
          const trialValues = this.fg.get('trial_membership')?.value;
          client.trial_membership = {
            trial_start_date: this.timeService.ConvertLocalDateTimeToUTC(trialValues.trial_start_date),
            notes: trialValues.notes,
            trial_preferred_time: this.timeService.GetUTCTimeFromDate(trialValues.trial_preferred_time),
            trial_owner_staff_id: trialValues.trial_owner_staff_id ? trialValues.trial_owner_staff_id.id : 0
          }
          client.trial_classes = null;
        }
        else if(this.fg.get('selected_trial_type') && this.fg.get('selected_trial_type')?.value === 'class')
        {
          const trialValues = this.fg.get('trial_classes')?.value;
          client.trial_classes = {trial_classes:trialValues};
          client.trial_membership = null;
        }
        else{
          client.trial_classes = null;  
          client.trial_membership = null;
        }

    }

    if(this.formType === 'walk-in-member' && this.CreateMember)
    {
      const tomorrowDate = new Date();
      tomorrowDate.setDate(tomorrowDate.getDate() + 1);
      client.task = {
        assigned_to_type: TaskAssignToType.ClientRep,
        assigned_to_staff_id: null,
        type: 'Tour',
        category_id: this.taskCategoryId,
        title: client.prospect.full_name,
        notes: 'New Lead Followup',
        priority: TaskPriority.High,
        call_id: null,
        email_id: null,
        sms_id: null,
        scheduled_date: this.timeService.ConvertLocalDateTimeToUTC(tomorrowDate),
      } as CreateTaskByOpportunityOnClientRequest;
    }

    if(this.formType === 'contact')
    {
      client.prospect.contact_id = this.contactData.contact_id;
    }
    return client;
  }

  GetClientReferrel(client: any) {
    this.fg.get('referredBy')?.setValue(client.clt_id);
  }

  getParentClientId(client: any) {
    this.fg.get('parent_client')?.setValue(client.clt_id);
    if(this.existClientForMobile && this.existClientForMobile.clt_id === client.clt_id)
    {
        const currentErrors = { ...this.MobileNoControl.errors };
        if(currentErrors) delete currentErrors['mobileExists'];
        this.MobileNoControl.setErrors(currentErrors);
        this.mobileAlreadyExists=false;
        this.cdr.detectChanges();
    }
    else if(this.existClientForEmail && this.existClientForEmail.clt_id === client.clt_id)
    {
        const currentErrors = { ...this.EmailIdControl.errors };
        if(currentErrors) delete currentErrors['emailExists'];
        delete currentErrors['emailExists'];
        this.EmailIdControl.setErrors(currentErrors);
        this.emailAlreadyExists=false;
        this.cdr.detectChanges();
    }
    else{
     if(this.existClientForMobile || this.existClientForEmail)
     {
       if(this.clientExistOnMobile)
       {
        this.MobileNoControl.setErrors({mobileExists:true});
        this.mobileAlreadyExists=true;
       }
       if(this.clientExistOnEmail)
       {
        this.EmailIdControl.setErrors({emailExists:true});
        this.emailAlreadyExists=true;
       }
       this.cdr.detectChanges();
    }
   }
  }


  removeClient(event: any) {
    this.fg.get('referredBy')?.setValue(0);
  }

  removeParentClient(event: any) {
    this.fg.get('parent_client')?.setValue(0);
    if(this.existClientForMobile || this.existClientForEmail)
      {
        if(this.existClientForMobile)
          {
            if(this.clientExistOnMobile)
            {
             this.MobileNoControl.setErrors({mobileExists:true});
             this.mobileAlreadyExists=true;
            }
         }
         if(this.existClientForEmail)
         {
          if(this.clientExistOnEmail)
            {
             this.EmailIdControl.setErrors({emailExists:true});
             this.emailAlreadyExists=true;
            }
         }
      }
  }


  SetPrefersLanguage(langCode: string) {
    return this.parseSupportedLang.find(
      (lang) => lang.LANG_CODE.toLocaleLowerCase() == langCode
    );
  }

  handleFileSelect(files: NgxFileDropEntry[]) {
    const file: NgxFileDropEntry = files[0];
    if (file.fileEntry.isFile) {
      const fileEntry = file.fileEntry as FileSystemFileEntry;
      fileEntry.file((file: File) => {
        this.showCropper(file);
      });
    }
  }

  showCropper(imageFile: any) {
    const header: string = 'common.IMAGE_CROP.CLIENT_IMAGE';
    this.refCropper = this.dialogService.open(ImageCropDialogComponent, {
      showHeader: false,
      baseZIndex: 10000,
      width: '70%',
      contentStyle: { 'max-height': '500px', 'max-width': '800px' },
      data: { imageFile, header },
    });
    this.refCropper.onClose.subscribe((croppedImage: any) => {
      if (croppedImage) {
        this.imageBase64 = croppedImage.image;
        this.fg.get('image')?.setValue(croppedImage.image);
        if(this.formEvent === 'update')
        {
          this.fg.get('image')?.markAsTouched();
          this.fg.get('image')?.updateValueAndValidity();
          this.fg.updateValueAndValidity();
        } 
        this.uploadProfilePicture = false;
        this.uploadSuccess = true;
        this.cdr.detectChanges();
      }
    });
  }

  saveProfilePicture(event: boolean) {
    if(event && this.imageBase64) {
      this.fg.get('image')?.setValue(this.imageBase64);
      if(this.formEvent === 'update')
      {
        this.fg.get('image')?.markAsTouched();
        this.fg.get('image')?.updateValueAndValidity();
        this.fg.markAsDirty();
        this.fg.updateValueAndValidity();
      } 
      this.uploadProfilePicture = false;
      this.uploadSuccess = true;
      this.cdr.detectChanges();
    }
    else {
      this.uploadProfilePicture = false;
      this.uploadSuccess = false;
      if(this.fg.get('image')?.value !== '') this.imageBase64 = '';
      this.cdr.detectChanges();
    }
  }

  // Helper methods for form data handling
  private findChannel(channel: string): any {
    return this.channelCollection.find(
      (c) => c.channel.toLowerCase() === channel.toLowerCase()
    );
  }

  private getSource(client: ClientById): string {
    if (this.isOtherChannel(client.channel)) {
      return client.source || '';
    }
    const channel = this.channelCollection.find((c) => c.channel.toLowerCase() === client.channel.toLowerCase());
    return channel?.source.find(
      (s: any) => s.toLowerCase() === client.source.toLowerCase()
    ) || '';
  }

  private findOrganization(organisation: any): Option | null {
    if (!organisation) return null;
    return this.organizations.find(o => o.value === organisation.id) || null;
  }

  private getPromotionalPreferences(client: any): string[] {
    const data= this.promotionalPreferenceCollection;
    return client.promotional_notification_channels_allowed && 
           client.promotional_notification_channels_allowed.length > 0 ? 
           data.map((o: any) => client.promotional_notification_channels_allowed.includes(o.code) ? o.code : null): 
      [];
  }

  private setupPromotionalPreferences(client: any | null): void {
    const data= this.promotionalPreferenceCollection;
    if (client && client.promotional_notification_channels_allowed?.length > 0) {
      const promotionalPreferences:any[] = [];
      client.promotional_notification_channels_allowed
        .filter((o: string) => o !== 'None')
        .forEach((o: string) => {
          promotionalPreferences.push(data.find((p: any) => p.code === o));
          if(o.toLowerCase() === 'email'){
            this.fg.get('email')?.setValue(true);
          }
          if(o.toLowerCase() === 'whatsapp'){
            this.fg.get('whatsapp')?.setValue(true);
          }
          if(o.toLowerCase() === 'sms'){
            this.fg.get('sms')?.setValue(true);
          }
        });

      promotionalPreferences.forEach((o: any) => {
        this.promotionPreferenceMap.set(o.code, o);
      });
      this.PromotionalPreferenceControl.setValue(Array.from(this.promotionPreferenceMap.values()).map((o: { code: any; }) => o.code));
      this.PromotionalPreferenceControl.markAsDirty();
      this.PromotionalPreferenceControl.updateValueAndValidity();
      this.cdr.detectChanges();
    }
    else {
      this.promotionalPreferenceCollection.forEach((o: any) => {
        this.promotionPreferenceMap.set(o.code, o);
      });
      this.PromotionalPreferenceControl.setValue(data);
      this.fg.get('sms')?.setValue(true);
      this.fg.get('whatsapp')?.setValue(true);
      this.fg.get('email')?.setValue(true);
      this.cdr.detectChanges();
    }
  }

  private isOtherChannel(channel: string): boolean {
    return channel.toLowerCase() === ClientChannel[ClientChannel.Other].toLowerCase() || 
    channel.toLowerCase() === 'other';
  }

  markFormGroupTouched(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
        this.markFormGroupTouched(control as UntypedFormGroup);
      } else {
        control?.markAsTouched({ onlySelf: true });
      }
    });
  }

  closeSidebar(event:boolean=false) {
    this.showProspect = false;
    this.hideProspect.emit(this.clientId$.value || 0);
    this.closeEditProfile.emit(event);
    this.ngOnDestroy();
    this.cdr.detectChanges();
  }

  selectTenant(event:any) {
    this.fg.get('tenant_id')?.setValue(event.id);
    this.cdr.detectChanges();
  }


  removePicture() {
    this.fg.get('image')?.setValue(null);
  }

  closeLostDeal(event:any) {
    this.showChooseReason = false;
    this.closeSidebar();
  }

  openClientSidebar(clientId:number) {
   this.GoToClientDetails.emit({clientId:clientId});
  }

  setBtnDisbale()
  {
    if(this.formEvent === 'create')
    {
      return false
    }
    else
    {
      return this.fg.touched ? false : true;
    }
  }

  get clientPicture_Base64(): string {
    return this.fg.get('image')?.value
      ? this.fg.get('image')?.value.replace('data:image/png;base64,', '')
      : '';
  }

  // Form Control Getters
  get NameControl(): UntypedFormControl {
    return this.fg.get('full_name') as UntypedFormControl;
  }


  get EmailIdControl(): UntypedFormControl {
    return this.fg.get('emailId') as UntypedFormControl;
  }

  get MobileNoControl(): UntypedFormControl {
    return this.fg.get('mobileNo') as UntypedFormControl;
  }

  get ChannelControl(): UntypedFormControl {
    return this.fg.get('channel') as UntypedFormControl;
  }

  get SourceControl(): UntypedFormControl {
    return this.fg.get('source') as UntypedFormControl;
  }

  get PromotionalPreferenceControl(): UntypedFormControl {
    return this.fg.get('promotionalPreference') as UntypedFormControl;
  }

  get CreateTaskControl(): UntypedFormControl {
    return this.fg.get('createTask') as UntypedFormControl;
  }

  get TaskTypeControl(): UntypedFormControl {
    return this.fg.get('taskType') as UntypedFormControl;
  }

  get TaskOnControl(): UntypedFormControl {
    return this.fg.get('taskOn') as UntypedFormControl;
  }

  get TaskOnDateControl(): UntypedFormControl {
    return this.fg.get('taskOnDate') as UntypedFormControl;
  }

  get InterestedInControl(): UntypedFormControl {
    return this.fg.get('interestedIn') as UntypedFormControl;
  }

  get SelectedGender(): UntypedFormControl {
    return this.fg.get('selectedGender') as UntypedFormControl;
  }

  get SelectedReferredBy(): UntypedFormControl {
    return this.fg.get('referredBy') as UntypedFormControl;
  }

  get NewRepresentative(): UntypedFormControl {
    return this.fg.get('newRepresentative') as UntypedFormControl;
  }

  get organizationFormControl(): UntypedFormControl {
    return this.fg.get('organization') as UntypedFormControl;
  }

  get CreateTask(): boolean {
    return this.fg.get('createTask')?.value as boolean;
  }

  get CreateOpportunity(): boolean {
    return this.fg.get('createOpportunity')?.value as boolean;
  }

  get CreateTour(): boolean {
    return this.fg.get('createWalkInTour')?.value as boolean;
  }

  get CreateTrial(): boolean {
    return this.fg.get('createWalkInTrial')?.value as boolean;
  }

  get CreateMember(): boolean {
    return this.fg.get('createWalkInMember')?.value as boolean;
  }

  get Source(): string[] {
    return this.ChannelControl.value?.source || [];
  }

  get DisableSource(): boolean {
    return !(this.Source && this.Source.length > 0);
  }

  get TaskOnDate(): Date {
    return this.TaskOnDateControl.value;
  }

  get MaleFemaleGender() {
    return this.EnumGender.filter((g) => g.key === '1' || g.key === '2')
      .slice()
      .reverse();
  }

  get OtherGender() {
    return this.EnumGender.filter((g) => !(g.key === '1' || g.key === '2'));
  }

  get InterestedInIds(): number[] {
    return this.InterestedInControl.value?.length > 0
      ? this.InterestedInControl.value.map((item: Option) => item.value)
      : [];
  }

  get Notes(): any {
    return this.fg.get('notes')?.value;
  }

  get DateOfBirthControl(): UntypedFormControl {
    return this.fg.get('dateOfBirth') as UntypedFormControl;
  }

  get EmergencyContactControl(): UntypedFormControl {
    return this.fg.get('emergencyContact') as UntypedFormControl;
  }

  get EmergencyNumberControl(): UntypedFormControl {
    return this.fg.get('emergencyNumber') as UntypedFormControl;
  }

  get MedicalAlertControl(): UntypedFormControl {
    return this.fg.get('medicalAlert') as UntypedFormControl;
  }

  get SecondMobileNoControl(): UntypedFormControl {
    return this.fg.get('secondMobileNo') as UntypedFormControl;
  }

  get PreferredLanguageControl(): UntypedFormControl {
    return this.fg.get('preferredLanguage') as UntypedFormControl;
  }

  get AddressPinCodeControl(): UntypedFormControl {
    return this.fg.get('address_pin_code') as UntypedFormControl;
  }

  get AddressControl(): UntypedFormControl {
    return this.fg.get('address') as UntypedFormControl;
  }

  get NationalIdControl(): UntypedFormControl {
    return this.fg.get('national_id') as UntypedFormControl;
  }

  get TaxNumberControl(): UntypedFormControl {
    return this.fg.get('taxNumber') as UntypedFormControl;
  }

  get MigrationIdControl(): UntypedFormControl {
    return this.fg.get('migrationId') as UntypedFormControl;
  }

  get NotesControl(): UntypedFormControl {
    return this.fg.get('notes') as UntypedFormControl;
  }

  get LinkedClientIdControl(): UntypedFormControl {
    return this.fg.get('parent_client') as UntypedFormControl;
  }

  get RelationshipControl(): UntypedFormControl {
    return this.fg.get('relation') as UntypedFormControl;
  }

  get IsChildClientFormControl(): UntypedFormControl {
    return this.fg.get('is_child') as UntypedFormControl;
  }

  get PreferedLanguageFormControl(): UntypedFormControl {
    return this.fg.get('preferredLanguage') as UntypedFormControl;
  }

  // Computed properties
  get isFormSubmitting(): boolean {
    return this.isFormSubmitting$.value;
  }

  get TenantIdControl(): UntypedFormControl {
    return this.fg.get('tenant_id') as UntypedFormControl;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Clear BehaviorSubjects
    this.showOtherGender.complete();
    this.clientId$.complete();
    this.isClientRepresentative.complete();
    this.selectedClientRep.complete();
    this.staffIds$.complete();
    this.isFormSubmitting$.complete();
    this.errorMessages$.complete();
  }

}
